import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import ReactGA from "react-ga4";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import {
  useLazyGetServiceProviderListQuery,
  useLoginMutation,
} from "../../../redux/api/auth/authAPI";
import AuthDTO from "../../../shared/oversight-core/dtos/request-dtos/auth-dto";
import { IServiceProvider } from "../../../shared/oversight-core/dtos/response-dtos/service-provider-list-response-dto";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { IHttpError } from "../../../shared/oversight-core/interfaces/http-errror";
import AppSelect, {
  Option,
} from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import CheckBox from "../../../shared/oversight-core/ui-elements/check-box/check-box";
import AppInput from "../../../shared/oversight-core/ui-elements/input/app-input";
import isMobileNumberVerified from "../../../shared/oversight-core/utils/isMobileNumberVerified";
import { showErrorMessage } from "../../../shared/oversight-core/utils/toast";
import CEB_IMG from "../assets/CEB.png";
import LECO_IMG from "../assets/LECO.png";
import OTHER_IMG from "../assets/OTHER.png";

const buildServiceProviderOptions = (
  serviceProviders: IServiceProvider[]
): Option[] => {
  const serviceProvidersList: Option[] = serviceProviders.map((sp) => {
    return {
      key: uuid(),
      value: sp.shortName,
      label: (
        <Row className="align-items-center" key={uuid()}>
          <Col className="col-1 text-center pe-3 pe-sm-0">
            <img
              src={
                sp.shortName === "CEB"
                  ? CEB_IMG
                  : sp.shortName === "LECO"
                  ? LECO_IMG
                  : OTHER_IMG
              }
              alt={`${
                sp.shortName === "CEB" || sp.shortName === "LECO"
                  ? sp.shortName
                  : "Other"
              } Logo`}
              width={24}
              height={24}
            />
          </Col>
          <Col>{sp.name}</Col>
        </Row>
      ),
    };
  });

  return serviceProvidersList;
};

const LoginForm = () => {
  const [serviceProviders, setServiceProviders] = useState<IServiceProvider[]>(
    []
  );
  const [selectedServiceProvider, setSelectedServiceProvider] =
    useState<Option>({ ...buildServiceProviderOptions(serviceProviders)[0] });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const [errorStyle, setErrorStyle] = useState(false);
  const navigate = useNavigate();
  const [login, { isError, error, isLoading }] = useLoginMutation();
  const [triggerServiceProviders, { isFetching: isFetchingServiceProviders }] =
    useLazyGetServiceProviderListQuery();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AuthDTO>();

  const onSubmit = (data: AuthDTO) => {
    login({
      ...data,
      rememberMe,
      serviceProvider: selectedServiceProvider.value,
    })
      .unwrap()
      .then(() => {
        ReactGA.event({
          category: "Successful",
          action: "Login",
        });
      })
      .catch((error: IHttpError) => {
        if (isMobileNumberVerified(error)) {
          navigate(AppRoute.VERIFY_MOBILE_NUMBER);
        } else {
          setErrorStyle(true);
          showErrorMessage(error.message || "Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      setErrorStyle(false);
    } else {
      setErrorStyle(true);
    }
  }, [errors]);

  useEffect(() => {
    triggerServiceProviders()
      .unwrap()
      .then((res) => {
        setServiceProviders(res.serviceProviders);
      })
      .catch(() => {
        setServiceProviders([]);
      });
  }, [triggerServiceProviders]);

  useEffect(() => {
    setSelectedServiceProvider({
      ...buildServiceProviderOptions(serviceProviders)[0],
    });
  }, [serviceProviders]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isError && error && "data" in error && (
        <div className="text-center error mt-2">
          Sorry, but the password or username you entered is incorrect. Please
          try again.
        </div>
      )}
      <div className="mt-5">
        <AppSelect
          className="mt-4"
          labelFontSize="14"
          selectedValue={selectedServiceProvider}
          label="Service Provider"
          options={[...buildServiceProviderOptions(serviceProviders)]}
          onChangeOption={(selectedServiceProviderOption) => {
            setSelectedServiceProvider(selectedServiceProviderOption);
          }}
          name="serviceProvider"
        />
        <AppInput
          className="mt-4"
          name="username"
          placeholder={"Enter your username or email"}
          label={"Username or Email"}
          register={register("username", {
            required: "Username or Email is required",
          })}
          errors={errors}
          isError={isError}
        />
        <AppInput
          className="mt-4"
          name="password"
          icon={!showPassword ? "visibility_off" : "visibility"}
          placeholder=" Enter your password"
          onIconClick={() => setShowPassword(!showPassword)}
          label="Password"
          type={showPassword ? "text" : "password"}
          register={register("password", {
            required: "Password is required",
          })}
          errors={errors}
          isError={isError}
        />
      </div>

      <Row className="mt-4">
        <Col>
          <CheckBox
            label="Remember me"
            checked={rememberMe}
            onChange={(checked) => setRememberMe(checked)}
          />
        </Col>

        <Col className="text-end">
          <Link to={AppRoute.FORGOT_PASSWORD}>Forgot password</Link>
        </Col>
      </Row>
      <AppButton
        text="Sign in"
        className={`mt-4 ${errorStyle && `manual-border-shadow`}`}
        variant="blue"
        isLoading={isLoading || isFetchingServiceProviders}
      />
      <Row className="justify-content-center mt-4">
        <Col className="text-center">
          Don&apos;t have an account?{" "}
          <Link to={AppRoute.REGISTER}>Register</Link>
        </Col>
      </Row>
    </form>
  );
};

export default LoginForm;
